import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { useQueryClient } from '@tanstack/react-query';
import { useInactivityTimer } from '@/hooks/useInactivityTimer';
import { useMultiTabSync } from '@/hooks/useMultiTabSync';
import * as Sentry from "@sentry/react";
import {
  validateInput, 
  signUpSchema, 
  signInSchema, 
  updatePasswordSchema,
  resetPasswordSchema,
  getFirstError
} from '@/lib/validation-schemas';
import { 
  mapError, 
  ApiError
} from '@/lib/api-errors';

interface EnterpriseAuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isWarning: boolean;
  remainingSeconds: number;
  signUp: (email: string, password: string, userData?: { first_name?: string; last_name?: string }) => Promise<{ error: ApiError | null }>;
  signIn: (email: string, password: string) => Promise<{ error: ApiError | null }>;
  signOut: (eventType?: 'user_logout' | 'auto_logout' | 'forced_logout' | 'global_logout', metadata?: Record<string, any>) => Promise<{ error: ApiError | null }>;
  resetPassword: (email: string) => Promise<{ error: ApiError | null }>;
  updatePassword: (password: string) => Promise<{ error: ApiError | null }>;
  resendConfirmation: (email: string) => Promise<{ error: ApiError | null }>;
  verifyEmailOtp: (email: string, token: string) => Promise<{ error: ApiError | null }>;
  stayActive: () => void;
  forceGlobalSignOut: () => Promise<{ error: ApiError | null }>;
}

const EnterpriseAuthContext = createContext<EnterpriseAuthContextType | undefined>(undefined);

function cleanSensitiveData(): void {
  try {
    const sensitiveKeys = [
      'assessment_progress', 'bookmarked_resources', 'taskSavedFilters',
      'criticalErrors', 'bundle_violations', 'user_preferences',
      'draft_data', 'temp_uploads'
    ];
    
    sensitiveKeys.forEach(key => {
      localStorage.removeItem(key);
      sessionStorage.removeItem(key);
    });
    
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('cache_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: { context: 'auth_error', operation: 'enterprise_auth', action: 'clear_data' }
    });
  }
}

export function EnterpriseAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  
  const queryClient = useQueryClient();
  const { broadcastGlobalLogout } = useMultiTabSync(user);
  
  const basicSignOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    return { error: error ? mapError(error, 'auth') : null };
  }, []);

  const handleAutoLogout = useCallback(() => {
    window.location.replace('/signed-out');
  }, []);

  const { isWarning, remainingSeconds, resetTimer } = useInactivityTimer(
    {
      timeoutMs: 25 * 60 * 1000, // 25 minutes
      warningMs: 23 * 60 * 1000  // 23 minutes
    },
    user,
    basicSignOut,
    handleAutoLogout
  );

  // Initialize auth
  useEffect(() => {
    const sessionTimeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      clearTimeout(sessionTimeout);
      setSession(currentSession);
      setUser(currentSession?.user || null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user || null);
        setLoading(false);

        if (event === 'SIGNED_OUT') {
          queryClient.clear();
          cleanSensitiveData();
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const signUp = useCallback(async (
    email: string,
    password: string,
    userData?: { first_name?: string; last_name?: string }
  ) => {
    const validation = validateInput(signUpSchema, { email, password });
    if (!validation.valid) {
      return { error: mapError(new Error(getFirstError(validation.errors)), 'auth') };
    }

    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: userData || {}
        }
      });

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error: mapError(error, 'auth') };
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const validation = validateInput(signInSchema, { email, password });
    if (!validation.valid) {
      return { error: mapError(new Error(getFirstError(validation.errors)), 'auth') };
    }

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password
      });

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error: mapError(error, 'auth') };
    }
  }, []);

  const signOut = useCallback(async (
    eventType: 'user_logout' | 'auto_logout' | 'forced_logout' | 'global_logout' = 'user_logout',
    metadata?: Record<string, any>
  ) => {
    sessionStorage.setItem('signing_out', 'true');

    try {
      if (eventType === 'global_logout') {
        await broadcastGlobalLogout();
      }

      queryClient.clear();
      cleanSensitiveData();

      const { error } = await supabase.auth.signOut();
      return { error: error ? mapError(error, 'auth') : null };
    } catch (error: any) {
      return { error: mapError(error, 'auth') };
    } finally {
      sessionStorage.removeItem('signing_out');
    }
  }, [broadcastGlobalLogout, queryClient]);

  const resetPassword = useCallback(async (email: string) => {
    const validation = validateInput(resetPasswordSchema, { email });
    if (!validation.valid) {
      return { error: mapError(new Error(getFirstError(validation.errors)), 'auth') };
    }

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`
      });

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error: mapError(error, 'auth') };
    }
  }, []);

  const updatePassword = useCallback(async (password: string) => {
    const validation = validateInput(updatePasswordSchema, { password });
    if (!validation.valid) {
      return { error: mapError(new Error(getFirstError(validation.errors)), 'auth') };
    }

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error: mapError(error, 'auth') };
    }
  }, []);

  const resendConfirmation = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email
      });

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error: mapError(error, 'auth') };
    }
  }, []);

  const verifyEmailOtp = useCallback(async (email: string, token: string) => {
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token,
        type: 'email'
      });

      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      return { error: mapError(error, 'auth') };
    }
  }, []);

  const forceGlobalSignOut = useCallback(async () => {
    return signOut('global_logout');
  }, [signOut]);

  const value = {
    user,
    session,
    loading,
    isWarning,
    remainingSeconds,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    resendConfirmation,
    verifyEmailOtp,
    stayActive: resetTimer,
    forceGlobalSignOut
  };

  return (
    <EnterpriseAuthContext.Provider value={value}>
      {children}
    </EnterpriseAuthContext.Provider>
  );
}

export function useEnterpriseAuth() {
  const context = useContext(EnterpriseAuthContext);
  if (context === undefined) {
    throw new Error('useEnterpriseAuth must be used within an EnterpriseAuthProvider');
  }
  return context;
}

// Backward compatibility export
export const useAuth = useEnterpriseAuth;
